import { useEffect, useState } from "react";
import { Link, NavLink, Route, Routes, useParams } from "react-router-dom";

import { MessagingProgram, PrivacyPolicy, SmsConsentForm, TermsOfService } from "./LegalPages";
import { MfaVerification, Portal, SignIn } from "./Portal";

const services = [
  {
    description: "Submit a request, add photos, and follow progress through confirmed completion.",
    eyebrow: "Resident support",
    title: "Maintenance that stays visible",
  },
  {
    description:
      "Find receipts, your signed lease, and tenant-facing notices in one private place.",
    eyebrow: "Accurate records",
    title: "The documents you should have",
  },
  {
    description:
      "Routine questions are acknowledged quickly, with a person brought in when needed.",
    eyebrow: "Clear communication",
    title: "A response you can rely on",
  },
];

interface PublicListing {
  description: string | null;
  displayName: string;
  generalLocation: string;
  media: Array<{ alt: string; sha256: string; source: string; url: string }>;
  occupancyStatus: "occupied" | "vacant";
  slug: string;
}

function usePublicListings() {
  const [listings, setListings] = useState<PublicListing[]>([]);
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    void fetch("/public/properties", { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error("Public property information was not available");
        }
        return (await response.json()) as { listings?: PublicListing[] };
      })
      .then((result) => {
        setListings(result.listings ?? []);
        setLoaded(true);
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        setFailed(true);
        setLoaded(true);
      });
    return () => controller.abort();
  }, []);
  return { failed, listings, loaded };
}

function PublicListingFailure() {
  return (
    <div className="notice-card" role="status">
      <h2>Public property information is temporarily unavailable.</h2>
      <p>
        Please try again later. No availability or resident information is inferred while the public
        listing service is unavailable.
      </p>
    </div>
  );
}

function Brand() {
  return (
    <Link aria-label="Potimus Properties home" className="brand" to="/">
      <span className="brand-mark" aria-hidden="true">
        P
      </span>
      <span>
        <strong>Potimus</strong>
        <small>Properties</small>
      </span>
    </Link>
  );
}

function Header() {
  return (
    <header className="site-header">
      <Brand />
      <nav aria-label="Primary navigation">
        <NavLink to="/homes">Homes</NavLink>
        <NavLink to="/resident">Resident support</NavLink>
        <NavLink className="button button-small" to="/sign-in">
          Resident sign in
        </NavLink>
      </nav>
    </header>
  );
}

function Home() {
  return (
    <>
      <section className="hero">
        <div className="hero-copy">
          <p className="eyebrow">Seaside, Oregon</p>
          <h1>Homes cared for with quiet attention.</h1>
          <p className="lede">
            Potimus Properties provides responsive, straightforward resident support and
            thoughtfully maintained coastal homes.
          </p>
          <div className="hero-actions">
            <Link className="button" to="/resident">
              Get resident support
            </Link>
            <Link className="text-link" to="/homes">
              View homes <span aria-hidden="true">→</span>
            </Link>
          </div>
        </div>
        <div className="hero-art" role="img" aria-label="Abstract view of the Oregon coast">
          <span className="sun" />
          <span className="headland headland-back" />
          <span className="headland headland-front" />
          <span className="wave wave-one" />
          <span className="wave wave-two" />
        </div>
      </section>

      <section className="service-section" aria-labelledby="service-heading">
        <div className="section-heading">
          <p className="eyebrow">How we help</p>
          <h2 id="service-heading">Simple systems. Human follow-through.</h2>
        </div>
        <div className="card-grid">
          {services.map((service) => (
            <article className="service-card" key={service.title}>
              <p className="eyebrow">{service.eyebrow}</p>
              <h3>{service.title}</h3>
              <p>{service.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="promise">
        <p className="eyebrow">Our operating promise</p>
        <p className="promise-text">
          Your message is acknowledged promptly, understood carefully, and followed through until
          the issue is resolved.
        </p>
      </section>
    </>
  );
}

function Homes() {
  const { failed, listings, loaded } = usePublicListings();
  return (
    <section className="content-page">
      <p className="eyebrow">Our homes</p>
      <h1>Coastal living, responsibly managed.</h1>
      {!loaded ? <p role="status">Loading approved property information…</p> : null}
      {loaded && failed ? <PublicListingFailure /> : null}
      {loaded && !failed && listings.length === 0 ? (
        <div className="notice-card">
          <h2>No public availability is listed right now.</h2>
          <p>
            To protect current residents, occupied-home addresses and resident details are never
            shown here. Approved vacancy information will appear when available.
          </p>
        </div>
      ) : null}
      {loaded && !failed && listings.length > 0 ? (
        <div className="listing-grid">
          {listings.map((listing) => (
            <article className="listing-card" key={listing.slug}>
              {listing.media[0] === undefined ? (
                <div aria-hidden="true" className="listing-placeholder" />
              ) : (
                <img
                  alt={listing.media[0].alt}
                  height="720"
                  loading="lazy"
                  src={listing.media[0].url}
                  width="1080"
                />
              )}
              <div>
                <p className="eyebrow">{listing.generalLocation}</p>
                <h2>{listing.displayName}</h2>
                <p>{listing.occupancyStatus === "vacant" ? "Available" : "Currently occupied"}</p>
                <Link className="text-link" to={`/homes/${listing.slug}`}>
                  View approved details
                </Link>
              </div>
            </article>
          ))}
        </div>
      ) : null}
    </section>
  );
}

function HomeDetail() {
  const { slug } = useParams();
  const { failed, listings, loaded } = usePublicListings();
  const listing = listings.find((candidate) => candidate.slug === slug);
  if (!loaded) {
    return (
      <section className="content-page">
        <p role="status">Loading approved property information…</p>
      </section>
    );
  }
  if (failed) {
    return (
      <section className="content-page">
        <PublicListingFailure />
      </section>
    );
  }
  if (listing === undefined) {
    return <NotFound />;
  }
  return (
    <section className="content-page property-detail">
      <p className="eyebrow">{listing.generalLocation}</p>
      <h1>{listing.displayName}</h1>
      <p className="lede">
        {listing.description ?? "Approved property details will be added when available."}
      </p>
      <p>
        <strong>Status:</strong>{" "}
        {listing.occupancyStatus === "vacant" ? "Available" : "Currently occupied"}
      </p>
      {listing.occupancyStatus === "occupied" ? (
        <p className="privacy-note">
          The exact address and all resident information are withheld to protect current residents.
        </p>
      ) : null}
      {listing.media.length > 0 ? (
        <div className="property-gallery">
          {listing.media.map((media) => (
            <img
              alt={media.alt}
              height="720"
              key={media.sha256}
              loading="lazy"
              src={media.url}
              width="1080"
            />
          ))}
        </div>
      ) : null}
    </section>
  );
}

function ResidentSupport() {
  return (
    <section className="content-page split-page">
      <div>
        <p className="eyebrow">Resident support</p>
        <h1>Tell us what you need.</h1>
        <p className="lede">
          For fire, a gas odor, danger from water near electricity, or a suspected break-in, call
          911 or the appropriate utility emergency service first.
        </p>
      </div>
      <div className="notice-card">
        <h2>Private portal activation pending</h2>
        <p>
          Online maintenance intake will be enabled only after security, privacy, messaging, and
          legal release checks are complete. Current residents should use their established contact
          method.
        </p>
        <Link className="button" to="/sign-in">
          Resident sign in
        </Link>
      </div>
    </section>
  );
}

function NotFound() {
  return (
    <section className="content-page">
      <p className="eyebrow">404</p>
      <h1>That page is not here.</h1>
      <Link className="text-link" to="/">
        Return home
      </Link>
    </section>
  );
}

export function App() {
  return (
    <div className="page-shell">
      <Header />
      <main id="main-content" tabIndex={-1}>
        <Routes>
          <Route element={<Home />} path="/" />
          <Route element={<Homes />} path="/homes" />
          <Route element={<HomeDetail />} path="/homes/:slug" />
          <Route element={<ResidentSupport />} path="/resident" />
          <Route element={<PrivacyPolicy />} path="/privacy" />
          <Route element={<TermsOfService />} path="/terms" />
          <Route element={<MessagingProgram />} path="/messaging" />
          <Route element={<SmsConsentForm />} path="/sms-consent" />
          <Route element={<SignIn />} path="/sign-in" />
          <Route element={<MfaVerification />} path="/mfa" />
          <Route element={<Portal />} path="/portal" />
          <Route element={<NotFound />} path="*" />
        </Routes>
      </main>
      <footer>
        <Brand />
        <div className="footer-copy">
          <p>Potimus Properties LLC · Seaside, Oregon</p>
          <nav aria-label="Legal and contact links" className="footer-links">
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
            <Link to="/messaging">Messaging</Link>
            <Link to="/sms-consent">SMS consent</Link>
            <a href="mailto:management@potimusproperties.com">Contact</a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
